import { Typography, Tag, Image, Space, Divider } from 'antd';
import { BankOutlined } from '@ant-design/icons';
import type { ContentPreviewResponse } from '@wisecure/api-client';
import { error as errorColors, neutral, primary } from '@/theme';

const { Text, Paragraph } = Typography;

const TYPE_LABELS: Record<string, string> = {
  POST: 'Publication',
  NEIGHBORING_REPORT: 'Signalement voisinage',
  NEIGHBORING_REPORT_COMMENT: 'Commentaire',
};

interface ContentPreviewProps {
  preview?: ContentPreviewResponse | null;
  moderationStatus?: 'VISIBLE' | 'HIDDEN';
}

export function ContentPreview({ preview, moderationStatus }: ContentPreviewProps) {
  if (!preview) {
    return (
      <Text type="secondary" style={{ fontStyle: 'italic' }}>
        Aperçu du contenu non disponible
      </Text>
    );
  }

  const typeLabel = TYPE_LABELS[preview.reportableType] ?? preview.reportableType;
  const statusTag = moderationStatus && (
    <Tag
      style={{
        backgroundColor: moderationStatus === 'HIDDEN' ? `${errorColors[400]}14` : `${neutral[300]}14`,
        color: moderationStatus === 'HIDDEN' ? errorColors[400] : neutral[500],
        borderColor: moderationStatus === 'HIDDEN' ? `${errorColors[400]}40` : `${neutral[300]}40`,
        borderRadius: 100,
      }}
    >
      {moderationStatus === 'HIDDEN' ? 'Masqué' : 'Visible'}
    </Tag>
  );

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Tag color="blue">{typeLabel}</Tag>
        {statusTag}
        {preview.reportableType === 'NEIGHBORING_REPORT' && preview.isOfficial && (
          <Tag
            icon={<BankOutlined />}
            style={{
              backgroundColor: `${primary[500]}14`,
              color: primary[600],
              borderColor: `${primary[500]}40`,
              borderRadius: 100,
            }}
          >
            {preview.municipalityName ?? 'Municipal'}
          </Tag>
        )}
      </Space>

      {preview.reportableType === 'POST' && (
        <>
          {preview.title && (
            <Text strong style={{ display: 'block', fontSize: 16, marginBottom: 8 }}>
              {String(preview.title)}
            </Text>
          )}
          {preview.description && (
            <Paragraph
              ellipsis={{ rows: 4, expandable: true, symbol: 'plus' }}
              style={{ marginBottom: 12, color: neutral[700] }}
            >
              {String(preview.description)}
            </Paragraph>
          )}
          {preview.imageUrls && preview.imageUrls.length > 0 && (
            <Image.PreviewGroup>
              <Space wrap size={8}>
                {preview.imageUrls.map((url, i) => (
                  <Image
                    key={i}
                    src={url}
                    width={80}
                    height={80}
                    style={{ objectFit: 'cover', borderRadius: 6 }}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                  />
                ))}
              </Space>
            </Image.PreviewGroup>
          )}
        </>
      )}

      {preview.reportableType === 'NEIGHBORING_REPORT' && (
        <>
          {preview.title && (
            <Text strong style={{ display: 'block', fontSize: 16, marginBottom: 8 }}>
              {String(preview.title)}
            </Text>
          )}
          {preview.description && (
            <Paragraph
              ellipsis={{ rows: 4, expandable: true, symbol: 'plus' }}
              style={{ marginBottom: 0, color: neutral[700] }}
            >
              {String(preview.description)}
            </Paragraph>
          )}
        </>
      )}

      {preview.reportableType === 'NEIGHBORING_REPORT_COMMENT' && (
        <div
          style={{
            borderLeft: `3px solid ${neutral[200]}`,
            paddingLeft: 12,
            marginBottom: 0,
          }}
        >
          <Paragraph
            italic
            ellipsis={{ rows: 4, expandable: true, symbol: 'plus' }}
            style={{ marginBottom: 0, color: neutral[700] }}
          >
            {String(preview.description ?? preview.title ?? '—')}
          </Paragraph>
        </div>
      )}

      <Divider style={{ margin: '12px 0' }} />
      <Space split={<Divider type="vertical" />}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {preview.author?.name ?? 'Auteur inconnu'}
        </Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {new Date(preview.createdAt).toLocaleDateString('fr-FR')}
        </Text>
      </Space>
    </div>
  );
}
